import { useEffect } from 'react';

/**
 * Fixes an accessibility violation (aria-hidden-focus) caused by Fluent UI's
 * internal tabster library. Tabster creates dummy `<i>` elements with
 * `tabindex="0"` and `aria-hidden="true"` for focus management, which violates
 * WCAG 4.1.2 — focusable content should not be inside aria-hidden elements.
 *
 * This hook uses a MutationObserver to detect these elements and set their
 * tabindex to "-1" so they are no longer in the tab order while remaining
 * functional for programmatic focus management.
 *
 * @see https://github.com/microsoft/tabster/issues/309
 */
export function useFixTabsterA11y(): void {
  useEffect(() => {
    const fixDummyElements = (root: Element | Document = document) => {
      const dummies = root.querySelectorAll<HTMLElement>(
        'i[data-tabster-dummy][aria-hidden="true"][tabindex="0"]'
      );
      dummies.forEach((el) => {
        el.setAttribute('tabindex', '-1');
      });
    };

    // Fix any elements already in the DOM
    fixDummyElements();

    // Observe for dynamically added elements
    const observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        mutation.addedNodes.forEach((node) => {
          if (node instanceof HTMLElement) {
            // Check the node itself
            if (
              node.matches('i[data-tabster-dummy][aria-hidden="true"][tabindex="0"]')
            ) {
              node.setAttribute('tabindex', '-1');
            }
            // Check descendants
            fixDummyElements(node);
          }
        });
        // Also handle attribute changes on existing elements
        if (
          mutation.type === 'attributes' &&
          mutation.target instanceof HTMLElement &&
          mutation.target.matches('i[data-tabster-dummy][aria-hidden="true"][tabindex="0"]')
        ) {
          mutation.target.setAttribute('tabindex', '-1');
        }
      });
    });

    observer.observe(document.body, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['tabindex'],
    });

    return () => observer.disconnect();
  }, []);
}
